import BigNumber from 'bignumber.js'
import { BIG_ZERO } from 'config/constants/number'
import { TokenAmount } from 'config/types'
import useAuthenticationModal from 'hooks/useAuthenticationModal'
import useKeydownEventListener from 'hooks/useKeydownEventListener'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import Column from 'layout/Components/Column'
import React, { useCallback, useEffect, useState } from 'react'
import { Trans } from 'react-i18next'
import { useAuth } from 'state/auth/hooks'
import styled from 'styled-components'
import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { BoxProps } from 'UIKit/Box/types'
import AutoBetConditionConfig from 'views/InhouseGame/components/AutoBetConditionConfig'
import Totalbet from 'views/InhouseGame/components/Controls/Totalbet'
import { InHouseGameButton, MineAnimation } from 'views/InhouseGame/components/styled'
import { InhouseGameBetResult } from 'views/InhouseGame/config/types'
import { useAutoBet } from 'views/InhouseGame/hooks/useAutoBet'
import { MineBet } from '../../config/types'
import { useClearFinishBet, useCurrentMineBet, useMineCompletedBetListener } from '../../hooks'
import MinesSelect from './components/MinesSelect'

const MineBetAction: React.FC<
  {
    isRunning: boolean
    disabled: boolean
    setTrigger: (isRun: boolean) => void
    hotkeyEnable?: boolean
    isStopping?: boolean
  } & BoxProps
> = ({ disabled, hotkeyEnable, isStopping, setTrigger, isRunning, ...props }) => {
  const { hasSession } = useAuth()

  const [onPresentLoginModal] = useAuthenticationModal()

  const eventHandler = useCallback(
    (event) => {
      if (event && event?.key === ' ') {
        event.preventDefault()
        if (!hotkeyEnable || disabled) return null
        setTrigger(!isRunning)
      }
    },
    [hotkeyEnable, disabled, setTrigger, isRunning],
  )
  useKeydownEventListener(eventHandler)

  return (
    <Column {...props}>
      {hasSession ? (
        <StyledButton disabled={disabled || isStopping} onClick={() => setTrigger(!isRunning)}>
          {isRunning ? (
            <Flex alignItems="center">
              <MineAnimation />
              <Box width="8px" />
              <Trans>Stop AutoBet</Trans>
            </Flex>
          ) : isStopping ? (
            <Flex alignItems="center">
              <MineAnimation />
              <Box width="8px" />
              <Trans>Stopping</Trans>
            </Flex>
          ) : (
            <Trans>Start AutoBet</Trans>
          )}
        </StyledButton>
      ) : (
        <StyledButton onClick={onPresentLoginModal}>
          <Trans>Login and Play</Trans>
        </StyledButton>
      )}
    </Column>
  )
}

const CompletedBetUpdater = ({ subscribe }) => {
  useMineCompletedBetListener(subscribe)
  return null
}

const MineAutoBet: React.FC<{
  hotkeyEnable?: boolean
  disabled?: boolean
  tokenAmount: TokenAmount
  mines: number
  maxBet: number
  setMines: (mines: number) => void
  onBet: (amount: BigNumber) => Promise<InhouseGameBetResult>
  selectedBlocks?: number[]
  onAutoBetStart: () => void
  onAutoBetEnd: () => void
}> = ({
  hotkeyEnable,
  mines,
  setMines,
  disabled,
  tokenAmount,
  selectedBlocks,
  onBet,
  onAutoBetStart,
  onAutoBetEnd,
  maxBet,
  ...props
}) => {
  const { isDesktop } = useMatchBreakpoints()
  const currentBet = useCurrentMineBet()
  const [totalBet, setTotalBet] = useState(0)
  const [isAutoStopping, setIsAutoStopping] = useState(false)
  const { config, isRunning, onEnd, onStart, subscribe, updateConfig } = useAutoBet({
    onBet,
    onNumberOfBetChanged: setTotalBet,
    maxLimit: maxBet,
    betDuration: 1000,
  })

  const clear = useClearFinishBet()

  useEffect(() => {
    clear()
  }, [])

  const onBetCompleted = useCallback(
    (bet: MineBet) => {
      const isWinBet = bet.multiplier > 0
      const payout = isWinBet ? bet.betAmount.amount.multipliedBy(bet.multiplier) : BIG_ZERO
      return subscribe({ payout, amount: bet.betAmount.amount })
    },
    [subscribe],
  )

  const toggleAutobet = (value: boolean) => {
    if (value) onStart(tokenAmount.amount, totalBet)
    else {
      onEnd()
      setIsAutoStopping(true)
    }
  }

  useEffect(() => {
    if (isRunning) {
      onAutoBetStart()
    }
  }, [isRunning])

  useEffect(() => {
    if (!isRunning && (!currentBet || currentBet.isCompleted)) {
      setTimeout(() => {
        onAutoBetEnd()
        clear()
        setIsAutoStopping(false)
      }, 1000)
    }
  }, [isRunning, currentBet?.isCompleted])

  return (
    <StyledWrapper {...props}>
      <CompletedBetUpdater subscribe={onBetCompleted} />
      <Column>
        {!isDesktop && (
          <MineBetAction
            hotkeyEnable={hotkeyEnable}
            mb="12px"
            disabled={disabled || selectedBlocks.length === 0}
            setTrigger={toggleAutobet}
            isRunning={isRunning}
            isStopping={isAutoStopping}
          />
        )}
        <MinesSelect mines={mines} onSelect={setMines} disabled={disabled || isRunning || isAutoStopping} mb="12px" />

        <Totalbet
          totalBet={totalBet}
          onChangeTotalBet={setTotalBet}
          disabled={disabled || isRunning || isAutoStopping}
          mb="12px"
          height="40px"
        />

        <AutoBetConditionConfig
          disabled={disabled || isAutoStopping}
          isRunning={isRunning}
          config={config}
          updateConfig={updateConfig}
          betToken={tokenAmount.token}
        />
      </Column>

      {isDesktop && (
        <MineBetAction
          hotkeyEnable={hotkeyEnable}
          disabled={disabled || selectedBlocks.length === 0}
          setTrigger={toggleAutobet}
          isRunning={isRunning}
          isStopping={isAutoStopping}
        />
      )}
    </StyledWrapper>
  )
}

const StyledWrapper = styled(Column)``

const StyledButton = styled(InHouseGameButton)`
  min-height: 42px !important;
  max-height: 42px !important;
`

export default MineAutoBet
